import { Box, Flex, Text } from '@radix-ui/themes';
import { SidebarHelper } from 'classes/helpers/sidebar.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { Folder } from 'components/main/sidebar/pitch-lists/full-menu/folder';
import { PitchList } from 'components/main/sidebar/pitch-lists/full-menu/pitch-list';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IMenuAction } from 'interfaces/i-menus';
import { ISidebarFolder, ISidebarPitchList } from 'interfaces/i-sidebar';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TeamsHelper } from 'lib_ts/classes/teams.helper';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { compact } from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMiscStore from 'stores/use-misc-store';
import { Controls } from './controls';
import './index.scss';

// matches Radix pl="2". Folder gets 8px of left padding per level of nesting.
const INDENT_PX = 8;

// Load rows 300px before they come into view.
const VIEWPORT_BUFFER_PX = 300;

const SORTED_PARENT_DEFS = [
  PitchListOwner.User,
  PitchListOwner.Machine,
  PitchListOwner.Team,
];

type SidebarCategoryRow = {
  kind: 'category';
  key: string;
  // the root folder, used to render the category label
  folder: ISidebarFolder;
  // whether this is the first category in the list (controls top spacing)
  first: boolean;
};

type SidebarPitchListRow = {
  kind: 'file';
  key: string;
  depth: number;
  file: ISidebarPitchList;
};

type SidebarFolderRow = {
  kind: 'folder';
  key: string;
  depth: number;
  folder: ISidebarFolder;
  isOpen: boolean;
};

type FlatRow = SidebarCategoryRow | SidebarPitchListRow | SidebarFolderRow;

const getFolderKey = (f: ISidebarFolder): string =>
  `${f.reference_type ? `|${f.reference_type}` : ''}|${f._parent_def}|${f._parent_id}|${f.pathDisplay}`;

/**
 * Walks a folder subtree and returns one FlatRow per visible item.
 * Recurses into a subfolder only when it's open in `openDict`, so the
 * returned array exactly matches what the user can currently see.
 */
const flattenFolderTree = (
  folder: ISidebarFolder,
  depth: number,
  openDict: Record<string, boolean | undefined>
): FlatRow[] => {
  // we display standalone pitch lists first, then folders
  const rows: FlatRow[] = folder.files.map((file) => ({
    kind: 'file',
    key: file.object._id,
    depth,
    file,
  }));

  for (const sub of folder.folders) {
    // hide folders whose entire subtree was filtered out by search
    if (sub.files.length + sub.folders.length === 0) {
      continue;
    }

    const key = getFolderKey(sub);
    const isOpen = !!openDict[key];

    rows.push({
      kind: 'folder',
      key,
      depth,
      folder: sub,
      isOpen,
    });

    if (!isOpen) {
      continue;
    }

    rows.push(...flattenFolderTree(sub, depth + 1, openDict));
  }

  return rows;
};

export const FullMenu = (props: {
  scrollParent: HTMLElement;
  onCreate: () => void;
  onRefresh: () => void;
  getFolderActions: (folder: ISidebarFolder) => IMenuAction[];
}) => {
  const lists = useListLibraryStore((s) => s.lists);
  const sidebarOpenPaths = useListLibraryStore((s) => s.sidebarOpenPaths);
  const sidebarFilters = useListLibraryStore((s) => s.sidebarFilters);

  const filterKeys = useMemo(
    () => MiscHelper.keyify(sidebarFilters.search),
    [sidebarFilters.search]
  );

  const rootFolders = useMemo(() => {
    const files = compact(
      lists.map((l) => {
        const safePathComps = `${l.folder}`
          .split('/')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);

        // create the file object
        const file: ISidebarPitchList = {
          reference_type: l.reference_type,

          _parent_def: l._parent_def,
          _parent_field: l._parent_field,
          _parent_id: l._parent_id,

          pathComponents: safePathComps,
          pathDisplay: safePathComps.join('/'),
          pathEnd: safePathComps.slice(-1)[0] ?? '',

          name: l.name,
          object: l,
        };

        if (
          filterKeys.length > 0 &&
          !ArrayHelper.hasSubstringIntersection(
            SidebarHelper.getFileKeys(file),
            filterKeys
          )
        ) {
          // this file needs to be filtered out
          return undefined;
        }

        return file;
      })
    );

    // sort the files by parent definition
    files.sort((a, b) => {
      const aIndex = SORTED_PARENT_DEFS.findIndex((d) => d === a._parent_def);
      const bIndex = SORTED_PARENT_DEFS.findIndex((d) => d === b._parent_def);
      return aIndex < bIndex ? -1 : 1;
    });

    const foldersDict: DictOf<ISidebarPitchList[]> = {
      ...ArrayHelper.groupByFn(
        files.filter((f) => !f.reference_type),
        (f) => f._parent_id
      ),
      ...ArrayHelper.groupByFn(
        files.filter((f) => f.reference_type),
        (f) => f.reference_type
      ),
    };

    // 0 => there is no folder value
    const depth = 0;

    const output: ISidebarFolder[] = compact(
      Object.values(foldersDict).map((values) => {
        if (!values) {
          // shouldn't trigger
          return undefined;
        }

        const folder = SidebarHelper.makeFolder(
          depth,
          [],
          values.filter((v) => v.pathComponents.length === depth),
          values.filter((v) => v.pathComponents.length > depth)
        );

        if (folder.files.length === 0 && folder.folders.length === 0) {
          // skip empty folders
          return undefined;
        }

        return folder;
      })
    );

    return output;
  }, [lists, sidebarFilters.category, filterKeys]);

  const current = useAuthStore((s) => s.current);
  const { machine } = useContext(MachineContext);

  const allRoots = useMemo(() => {
    const output = rootFolders.slice();

    if (!current.placeholder_folders) {
      return output;
    }

    const personalRoot = output.find(
      (f) => f.depth === 0 && f._parent_def === PitchListOwner.User
    );

    if (personalRoot) {
      // only add placeholders to the personal root
      const orgs = TeamsHelper.getTeamOptions(current.league, true);

      // sort the teams by their names
      orgs.sort((a, b) => StringHelper.safeSort(a.label, b.label));

      orgs.forEach((team) => {
        const pathComponents = [team.value];

        const existingTeam = personalRoot.folders.find((f) =>
          ArrayHelper.equals(f.pathComponents, pathComponents)
        );

        const tFolder: ISidebarFolder = existingTeam ?? {
          depth: 1,
          _parent_def: PitchListOwner.User,
          _parent_id: current.userID,
          _parent_field: 'lists',

          reference_type: undefined,

          pathComponents: pathComponents,
          pathDisplay: pathComponents.join('/'),
          pathEnd: pathComponents.slice(-1)[0] ?? '',

          files: [],
          folders: [],
          placeholder: true,
        };

        if (!existingTeam) {
          // avoid duplicate entries, but continue processing in case the subfolders need to be created
          personalRoot.folders.push(tFolder);
        }

        ['Starters', 'Relievers'].forEach((sub) => {
          const pathComponents = [team.value, sub];

          if (
            tFolder.folders.findIndex((f) =>
              ArrayHelper.equals(pathComponents, f.pathComponents)
            ) !== -1
          ) {
            // avoid duplicate entries
            return;
          }

          const sFolder: ISidebarFolder = {
            depth: 2,
            _parent_def: PitchListOwner.User,
            _parent_id: current.userID,
            _parent_field: 'lists',

            reference_type: undefined,

            pathComponents: pathComponents,
            pathDisplay: pathComponents.join('/'),
            pathEnd: pathComponents.slice(-1)[0] ?? '',

            files: [],
            folders: [],
            placeholder: true,
          };
          tFolder.folders.push(sFolder);
        });

        return tFolder;
      });
    }

    return output;
  }, [current, rootFolders]);

  const filteredRoots = useMemo(() => {
    const { category } = sidebarFilters;

    return (
      allRoots
        // only render non-empty stuff
        .filter((r) => r.folders.length > 0 || r.files.length > 0)
        // only render the selected category/root
        .filter((s) => {
          if (!category) {
            // all lists
            return true;
          }

          // filtering by owner
          switch (category) {
            case PitchListOwner.Team: {
              return (
                !s.reference_type &&
                s._parent_def === category &&
                s._parent_id === current.teamID
              );
            }

            case PitchListOwner.Machine: {
              return (
                !s.reference_type &&
                s._parent_def === category &&
                s._parent_id === machine._id
              );
            }

            case PitchListOwner.User: {
              return (
                !s.reference_type &&
                s._parent_def === category &&
                s._parent_id === current.userID
              );
            }

            case ReferenceListType.BallTypeCalibration:
            case ReferenceListType.Repeatability:
            case ReferenceListType.BurnInTest:
            case ReferenceListType.Sample: {
              // ignore owner when filtering by type
              return s.reference_type === category;
            }

            default: {
              return false;
            }
          }
        })
    );
  }, [current, machine, allRoots, sidebarFilters]);

  // auto-expand all folders when there's an active search so matches are visible
  useEffect(() => {
    if (filterKeys.length === 0) {
      return;
    }

    useListLibraryStore.setState((prev) => {
      const next = { ...prev.sidebarOpenPaths };
      let changed = false;

      const visit = (folder: ISidebarFolder): void => {
        for (const sub of folder.folders) {
          const k = getFolderKey(sub);
          if (!next[k]) {
            next[k] = true;
            changed = true;
          }
          visit(sub);
        }
      };

      filteredRoots.forEach((root) => visit(root));

      if (!changed) {
        // no changes, so return the previous state to avoid unnecessary re-renders
        return prev;
      }

      return {
        sidebarOpenPaths: next,
      };
    });
  }, [filteredRoots, filterKeys]);

  // Flatten every root's visible tree into a single list of rows + category labels.
  const rows = useMemo(() => {
    const output: FlatRow[] = [];
    let firstCategory = true;

    for (const root of filteredRoots) {
      const rootRows = flattenFolderTree(root, 0, sidebarOpenPaths);

      // nothing visible in this root, skip it (and its category label)
      if (rootRows.length === 0) {
        continue;
      }

      // hide the category label when filtering by category since it's redundant
      if (!sidebarFilters.category) {
        output.push({
          kind: 'category',
          key: `category${getFolderKey(root)}`,
          folder: root,
          first: firstCategory,
        });
        firstCategory = false;
      }

      output.push(...rootRows);
    }

    return output;
  }, [filteredRoots, sidebarOpenPaths, sidebarFilters.category]);

  const noResults = rows.length === 0;

  return (
    <ErrorBoundary identifier="FullMenu">
      <Flex className="FullMenu" direction="column" gap={RADIX.FLEX.GAP.SM}>
        <Box>
          <Controls onCreate={props.onCreate} onRefresh={props.onRefresh} />
        </Box>

        {noResults ? (
          <Flex
            direction="column"
            justify="center"
            align="center"
            gap="2"
            pt="3"
            pb="3"
            pl="2"
            pr="2"
          >
            <img
              height="48"
              width="48"
              alt="no search results"
              src={useMiscStore.getState().getThemeImgPath('search-empty.svg')}
            />

            <Text size="1" align="center" color={RADIX.COLOR.NEUTRAL}>
              {t('pl.no-search-results-msg')}
            </Text>
          </Flex>
        ) : (
          <Virtuoso
            customScrollParent={props.scrollParent}
            data={rows}
            increaseViewportBy={VIEWPORT_BUFFER_PX}
            computeItemKey={(_, row) => row.key}
            itemContent={(_, row) => {
              if (row.kind === 'category') {
                return (
                  <Box pt={row.first ? '1' : '4'} pb="1" pl="2" pr="2">
                    <Text size="1" color={RADIX.COLOR.NEUTRAL} truncate>
                      {SidebarHelper.getFolderName(
                        row.folder,
                        machine.machineID
                      )}
                    </Text>
                  </Box>
                );
              }

              return (
                <Box
                  data-identifier="SidebarRow"
                  style={{ paddingLeft: row.depth * INDENT_PX }}
                >
                  {row.kind === 'file' ? (
                    <PitchList file={row.file} />
                  ) : (
                    <Folder
                      folder={row.folder}
                      isOpen={row.isOpen}
                      onToggle={() =>
                        useListLibraryStore.setState((prev) => ({
                          sidebarOpenPaths: {
                            ...prev.sidebarOpenPaths,
                            [row.key]: !prev.sidebarOpenPaths[row.key],
                          },
                        }))
                      }
                      getFolderActions={props.getFolderActions}
                    />
                  )}
                </Box>
              );
            }}
          />
        )}
      </Flex>
    </ErrorBoundary>
  );
};
